import React, { useEffect, useState } from 'react';
import api, { API_BASE } from '../lib/api';
import { Download, Users, ShoppingCart, X, Lock } from 'lucide-react';

const ORDER_STATUSES = [
  'PENDING_PAYMENT', 'AWAITING_VERIFICATION', 'CONFIRMED',
  'PROCESSING', 'PACKED', 'SHIPPED', 'DELIVERED', 'CANCELLED',
];
const PAYMENT_STATUSES = [
  'PENDING', 'SCREENSHOT_UPLOADED', 'VERIFIED', 'UNDERPAID', 'REJECTED',
];

// 30-day default range (matches backend default).
function defaultRange() {
  const to = new Date();
  const from = new Date(); from.setDate(from.getDate() - 30);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

function toCSV(kind, filters) {
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v && String(v).length) qs.set(k, v);
  });
  return `${API_BASE}/export/${kind}?${qs.toString()}`;
}

function ExportModal({ kind, onClose, onDownloaded }) {
  const rng = defaultRange();
  const [dateFrom, setDateFrom] = useState(rng.from);
  const [dateTo, setDateTo] = useState(rng.to);
  const [buyerStatus, setBuyerStatus] = useState('');   // tag CSV
  const [orderActivity, setOrderActivity] = useState('all');
  const [orderStatus, setOrderStatus] = useState('');   // multi-select CSV
  const [paymentStatus, setPaymentStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const toggleSet = (current, val) => {
    const set = new Set((current || '').split(',').map((s) => s.trim()).filter(Boolean));
    if (set.has(val)) set.delete(val); else set.add(val);
    return [...set].join(',');
  };

  const handleDownload = async () => {
    setBusy(true); setErr('');
    try {
      const filters = kind === 'buyers'
        ? { date_from: dateFrom, date_to: dateTo, buyer_status: buyerStatus, order_activity: orderActivity }
        : { date_from: dateFrom, date_to: dateTo, order_status: orderStatus, payment_status: paymentStatus };
      const url = toCSV(kind, filters);
      // Use axios so we send the JWT + get a Blob back.
      const token = localStorage.getItem('fab_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Export failed (${res.status})`);
      }
      const count = res.headers.get('X-Record-Count') || '?';
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${kind}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      onDownloaded?.({ kind, count });
      onClose();
    } catch (e) {
      setErr(e.message || 'Download failed');
    } finally { setBusy(false); }
  };

  const title = kind === 'buyers' ? 'Export Buyers' : 'Export Orders';
  const Icon = kind === 'buyers' ? Users : ShoppingCart;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-3 sm:p-0"
      onClick={onClose}
      data-testid={`export-${kind}-modal`}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-2xl shadow-xl p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-fab-primary-lighter text-fab-primary flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="font-display font-bold text-fab-text text-lg">{title}</h3>
          </div>
          <button
            className="text-fab-text-muted hover:text-fab-text"
            onClick={onClose}
            aria-label="Close"
            data-testid={`export-${kind}-close`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">From</span>
              <input
                type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1 w-full border border-fab-primary-lighter rounded-lg px-3 py-2 text-sm"
                data-testid={`export-${kind}-date-from`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">To</span>
              <input
                type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="mt-1 w-full border border-fab-primary-lighter rounded-lg px-3 py-2 text-sm"
                data-testid={`export-${kind}-date-to`}
              />
            </label>
          </div>

          {kind === 'buyers' ? (
            <>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Buyer Status / Tags (comma-separated)</span>
                <input
                  type="text" placeholder="e.g. hot_lead, vip, new"
                  value={buyerStatus} onChange={(e) => setBuyerStatus(e.target.value)}
                  className="mt-1 w-full border border-fab-primary-lighter rounded-lg px-3 py-2 text-sm"
                  data-testid="export-buyers-status"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Order Activity</span>
                <select
                  value={orderActivity} onChange={(e) => setOrderActivity(e.target.value)}
                  className="mt-1 w-full border border-fab-primary-lighter rounded-lg px-3 py-2 text-sm"
                  data-testid="export-buyers-activity"
                >
                  <option value="all">All buyers</option>
                  <option value="with_orders">Buyers with orders</option>
                  <option value="no_orders">Buyers with no orders</option>
                </select>
              </label>
            </>
          ) : (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Order Status</div>
                <div className="flex flex-wrap gap-1.5" data-testid="export-orders-status-list">
                  {ORDER_STATUSES.map((s) => {
                    const active = (orderStatus || '').split(',').includes(s);
                    return (
                      <button
                        key={s} type="button"
                        onClick={() => setOrderStatus(toggleSet(orderStatus, s))}
                        className={`text-[11px] px-2.5 py-1 rounded-full border ${
                          active ? 'bg-fab-primary text-white border-fab-primary'
                                 : 'bg-white text-fab-text border-fab-primary-lighter'
                        }`}
                        data-testid={`export-orders-status-${s}`}
                      >{s}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Payment Status</div>
                <div className="flex flex-wrap gap-1.5" data-testid="export-orders-payment-list">
                  {PAYMENT_STATUSES.map((s) => {
                    const active = (paymentStatus || '').split(',').includes(s);
                    return (
                      <button
                        key={s} type="button"
                        onClick={() => setPaymentStatus(toggleSet(paymentStatus, s))}
                        className={`text-[11px] px-2.5 py-1 rounded-full border ${
                          active ? 'bg-fab-primary text-white border-fab-primary'
                                 : 'bg-white text-fab-text border-fab-primary-lighter'
                        }`}
                        data-testid={`export-orders-payment-${s}`}
                      >{s}</button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {err && (
            <div className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`export-${kind}-error`}>
              {err}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            className="px-4 py-2 rounded-lg text-fab-text-secondary text-sm hover:bg-fab-elevated"
            onClick={onClose} disabled={busy}
            data-testid={`export-${kind}-cancel`}
          >Cancel</button>
          <button
            className="px-4 py-2 rounded-lg bg-fab-primary text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
            onClick={handleDownload} disabled={busy}
            data-testid={`export-${kind}-download`}
          >
            <Download className="w-4 h-4" />
            {busy ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExportPanel({ compact = false }) {
  const [access, setAccess] = useState(null);
  const [open, setOpen] = useState(null);  // 'buyers' | 'orders' | null
  const [toast, setToast] = useState('');
  const [chooser, setChooser] = useState(false);

  useEffect(() => {
    api.get('/export/access').then(({ data }) => setAccess(data)).catch(() => setAccess({ can_export: false }));
  }, []);

  if (!access) return null;
  const locked = !access.can_export;

  if (compact) {
    return (
      <div
        className="bg-white border border-fab-primary-lighter rounded-xl p-4 sm:p-5 flex flex-col h-full"
        data-testid="export-panel"
      >
        <div className="w-10 h-10 rounded-xl bg-fab-primary-lighter text-fab-primary flex items-center justify-center mb-3">
          <Download className="w-5 h-5" />
        </div>
        <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold flex items-center gap-1.5">
          Buyer & Order
          {access.source === 'promotion' && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" data-testid="export-promo-badge">
              Promo
            </span>
          )}
          {access.source === 'trial' && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-fab-primary-lighter text-fab-primary px-1.5 py-0.5 rounded" data-testid="export-trial-badge">
              Trial
            </span>
          )}
        </div>
        <div className="font-display font-bold text-fab-text text-[15px] leading-tight">CSV Report</div>
        <div className="text-[11.5px] text-fab-text-muted mt-1 flex-1">
          {locked ? 'Available on Pro & Enterprise.' : 'Export buyers & orders as CSV'}
        </div>
        <div className="mt-3 relative">
          <button
            className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-[13px] font-semibold ${
              locked ? 'bg-fab-elevated text-fab-text-muted cursor-not-allowed'
                     : 'bg-fab-primary text-white hover:brightness-105'
            }`}
            disabled={locked}
            onClick={() => setChooser((v) => !v)}
            data-testid="export-now-btn"
          >
            {locked ? <Lock className="w-4 h-4" /> : <Download className="w-4 h-4" />}
            Export Now
          </button>
          {chooser && !locked && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-fab-primary-lighter rounded-lg shadow-lg z-10 overflow-hidden" data-testid="export-chooser">
              <button
                className="w-full px-3 py-2 text-left text-[13px] hover:bg-fab-elevated flex items-center gap-2"
                onClick={() => { setChooser(false); setOpen('buyers'); }}
                data-testid="export-choose-buyers"
              ><Users className="w-4 h-4 text-fab-primary" /> Export Buyers</button>
              <button
                className="w-full px-3 py-2 text-left text-[13px] hover:bg-fab-elevated flex items-center gap-2 border-t border-fab-primary-lighter"
                onClick={() => { setChooser(false); setOpen('orders'); }}
                data-testid="export-choose-orders"
              ><ShoppingCart className="w-4 h-4 text-fab-primary" /> Export Orders</button>
            </div>
          )}
        </div>
        {toast && (
          <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="export-toast">
            {toast}
          </div>
        )}
        {open && (
          <ExportModal
            kind={open}
            onClose={() => setOpen(null)}
            onDownloaded={({ kind, count }) => setToast(`${kind === 'buyers' ? 'Buyers' : 'Orders'} CSV downloaded (${count} rows).`)}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-fab-primary-lighter rounded-xl p-4 sm:p-5"
      data-testid="export-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Data Export</div>
            {access.source === 'promotion' && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" data-testid="export-promo-badge">
                Promo · {access.plan}
              </span>
            )}
          </div>
          <div className="font-display font-bold text-fab-text text-[17px] mt-0.5">Buyer & Order CSV Export</div>
          <div className="text-[12px] text-fab-text-secondary mt-0.5">
            {locked
              ? 'Available on Pro & Enterprise plans. Upgrade to download CSVs of your buyers and orders.'
              : 'Download filtered CSVs. Exports are scoped to your account only.'}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${
              locked ? 'bg-fab-elevated text-fab-text-muted cursor-not-allowed'
                     : 'bg-fab-primary text-white hover:brightness-105'
            }`}
            disabled={locked}
            onClick={() => setOpen('buyers')}
            data-testid="export-buyers-btn"
          >
            {locked ? <Lock className="w-4 h-4" /> : <Users className="w-4 h-4" />}
            Export Buyers
          </button>
          <button
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${
              locked ? 'bg-fab-elevated text-fab-text-muted cursor-not-allowed'
                     : 'bg-fab-primary text-white hover:brightness-105'
            }`}
            disabled={locked}
            onClick={() => setOpen('orders')}
            data-testid="export-orders-btn"
          >
            {locked ? <Lock className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
            Export Orders
          </button>
        </div>
      </div>

      {toast && (
        <div className="mt-3 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="export-toast">
          {toast}
        </div>
      )}

      {open && (
        <ExportModal
          kind={open}
          onClose={() => setOpen(null)}
          onDownloaded={({ kind, count }) => setToast(`${kind === 'buyers' ? 'Buyers' : 'Orders'} CSV downloaded (${count} rows).`)}
        />
      )}
    </div>
  );
}
